import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, doc, getDoc } from "firebase/firestore";
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { format, subMonths, subYears } from "date-fns";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#6EE7B7", "#F87171", "#2DD4BF"];
const GRADIENT_COLORS = [
  { start: "#3B82F6", end: "#60A5FA" },
  { start: "#10B981", end: "#34D399" },
  { start: "#F59E0B", end: "#FBBF24" },
  { start: "#EF4444", end: "#F87171" },
];

// List of category collection IDs (corrected to match your data)
const CATEGORY_COLLECTIONS = [
  "DesignerMetal&MetalFrame", // Corrected from DesignerMetal_MetalFrame
  "Accessories",
  "FrameLess",
  "FullFrame",
  "HalfFrame",
  "Kids-Frame",
  "Kids-Sunglass",
  "SafetyGlassess",
  "Sunglassess",
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-gray-800">{label}</p>
        <p className="text-sm text-gray-600">Month: {data.month || ""}</p>
        {payload.map((entry, index) => (
          <p key={`tooltip-${index}`} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.name === "Sales" ? `₹${entry.value.toFixed(2)}` : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ payload }) => (
  <div className="flex flex-wrap justify-center gap-4 mt-4">
    {payload.map((entry, index) => (
      <div key={`legend-${index}`} className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
        <span className="text-sm text-gray-700">{entry.value}</span>
      </div>
    ))}
  </div>
);

const CustomTick = (props) => {
  const { x, y, payload } = props;
  const { value, month, dateOrdered } = payload.value;

  const isValidDate = dateOrdered && !isNaN(new Date(dateOrdered).getTime());
  const fullDate = isValidDate ? format(new Date(dateOrdered), "dd MMM yyyy") : null;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={-15} textAnchor="middle" fill="#4B5563" fontSize={14}>
        {value}
      </text>
      {isValidDate && (
        <>
          <text x={0} y={5} textAnchor="middle" fill="#6B7280" fontSize={12}>
            {month}
          </text>
          <text x={0} y={20} textAnchor="middle" fill="#6B7280" fontSize={10}>
            {fullDate}
          </text>
        </>
      )}
    </g>
  );
};

export default function ViewAnalysis() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeRange, setTimeRange] = useState("all");
  const [hasInvalidData, setHasInvalidData] = useState(false);
  const [unmatchedProductIds, setUnmatchedProductIds] = useState([]);

  const db = getFirestore();

  // Fetch orders and map to categories
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const ordersRef = collection(db, "detailsorder");
        const querySnapshot = await getDocs(ordersRef);

        let invalidDataFound = false;
        const allOrders = [];
        const productIdToCategory = {};
        const unmatchedIds = new Set();

        // Step 1: Collect all orders and unique productIDs
        const productIds = new Set();
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.orders && Array.isArray(data.orders)) {
            data.orders.forEach((order) => {
              const totalAmount = parseFloat(order.totalAmount);
              if (isNaN(totalAmount)) {
                console.warn(`Invalid totalAmount for order in ${docSnap.id}:`, order.totalAmount);
                invalidDataFound = true;
              }

              const productId = order.productDetails?.productID; // Corrected to match your schema
              if (productId) {
                productIds.add(productId);
              } else {
                console.warn(`No productID found for order in ${docSnap.id}:`, order);
                invalidDataFound = true;
                unmatchedIds.add(`unknown-${docSnap.id}-${Date.now()}`);
              }

              const validAmount = !isNaN(totalAmount) ? Math.abs(totalAmount) : 0;
              const timestamp = order.timestamp
                ? new Date(order.timestamp)
                : new Date();

              allOrders.push({
                id: `${docSnap.id}-${timestamp || Date.now()}`,
                productId,
                category: null, // Will be determined later
                dateOrdered: timestamp,
                totalAmount: validAmount,
                lensOption: order.lensOption?.name || "Only Frame",
                docId: docSnap.id,
              });
            });
          } else {
            console.warn(`No orders array found in document ${docSnap.id}`);
            invalidDataFound = true;
          }
        });

        // Step 2: Fetch category mappings for productIDs
        const productIdArray = Array.from(productIds);
        for (const collectionId of CATEGORY_COLLECTIONS) {
          const promises = productIdArray.map(async (productId) => {
            const productRef = doc(db, collectionId, productId);
            const productSnap = await getDoc(productRef);
            if (productSnap.exists()) {
              productIdToCategory[productId] = collectionId;
            }
          });
          await Promise.all(promises);
        }

        // Step 3: Assign categories to orders
        const finalOrders = allOrders.map((order) => {
          let category = productIdToCategory[order.productId] || "Unknown";
          if (category === "Unknown" && order.productId) {
            unmatchedIds.add(order.productId);
            console.warn(
              `Product ID ${order.productId} from document ${order.docId} not found in any category collection`
            );
            invalidDataFound = true;
          }
          return {
            ...order,
            category,
          };
        });

        // Step 4: Filter orders based on timeRange
        const filteredOrders = finalOrders.filter((order) => {
          const now = new Date();
          if (timeRange === "month") {
            return order.dateOrdered >= subMonths(now, 1);
          } else if (timeRange === "year") {
            return order.dateOrdered >= subYears(now, 1);
          }
          return true;
        });

        setHasInvalidData(invalidDataFound);
        setUnmatchedProductIds(Array.from(unmatchedIds));
        setOrders(filteredOrders);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to load order data");
        setLoading(false);
      }
    };

    fetchOrders();
  }, [db, timeRange]);

  // Calculate financial totals
  const totalOrders = orders.length;
  const totalRevenue = orders.reduce((sum, order) => sum + order.totalAmount, 0);
  const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  // Process data for category distribution with month
  const getCategoryDistribution = () => {
    const categoryCount = {};
    const categoryLatestMonth = {};

    orders.forEach((order) => {
      const category = order.category || "Unknown";
      const date = order.dateOrdered;
      const month = timeRange === "month" ? format(date, "dd MMM") : format(date, "MMM");

      categoryCount[category] = (categoryCount[category] || 0) + 1;

      if (!categoryLatestMonth[category] || date > categoryLatestMonth[category].date) {
        categoryLatestMonth[category] = { month, date };
      }
    });

    return Object.entries(categoryCount)
      .map(([name, value]) => ({
        name,
        value,
        month: categoryLatestMonth[name]?.month || "",
      }))
      .sort((a, b) => b.value - a.value);
  };

  // Process data for financial trends (by order index)
  const getFinancialTrendsData = () => {
    const trendsData = {};
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    orders.forEach((order, index) => {
      const date = order.dateOrdered;
      const orderIndex = index + 1;
      const month = timeRange === "month" ? format(date, "dd MMM") : monthNames[date.getMonth()];

      if (!trendsData[orderIndex]) {
        trendsData[orderIndex] = {
          orderIndex,
          month,
          dateOrdered: date,
          sales: 0,
        };
      }

      trendsData[orderIndex].sales += order.totalAmount;
    });

    return Object.values(trendsData).sort((a, b) => a.orderIndex - b.orderIndex);
  };

  // Process category-wise data (by order index)
  const getCategoryWiseData = () => {
    const categoryData = {};
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    orders.forEach((order, index) => {
      const category = order.category || "Unknown";
      const date = order.dateOrdered;
      const orderIndex = index + 1;

      if (!categoryData[category]) {
        categoryData[category] = {};
      }

      if (!categoryData[category][orderIndex]) {
        categoryData[category][orderIndex] = {
          orderIndex,
          month: timeRange === "month" ? format(date, "dd MMM") : monthNames[date.getMonth()],
          dateOrdered: date,
          sales: 0,
        };
      }

      categoryData[category][orderIndex].sales += order.totalAmount;
    });

    return categoryData;
  };

  const categoryDistribution = getCategoryDistribution();
  const financialTrendsData = getFinancialTrendsData();
  const categoryWiseData = getCategoryWiseData();

  return (
    <>
      <style>
        {`
          .recharts-wrapper {
            transition: all 0.3s ease-in-out;
          }
          .recharts-surface {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            border-radius: 8px;
          }
          .chart-container {
            background: linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%);
            padding: 1.5rem;
            border-radius: 12px;
            margin-bottom: 2rem;
          }
          .time-range-button {
            transition: all 0.2s ease;
          }
          .time-range-button:hover {
            transform: translateY(-1px);
          }
          .summary-card {
            transition: transform 0.2s ease;
          }
          .summary-card:hover {
            transform: translateY(-4px);
          }
        `}
      </style>
      <Header />
      <div className="container mx-auto px-4 py-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        {hasInvalidData && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-lg">
            <div className="flex items-center">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="ml-3 text-sm text-yellow-700">
                Warning: Some orders have invalid data.{" "}
                {unmatchedProductIds.length > 0 && (
                  <>
                    Unmatched product IDs: {unmatchedProductIds.join(", ")}.{" "}
                  </>
                )}
                Check console for details.
              </p>
            </div>
          </div>
        )}

        <h1 className="text-4xl font-extrabold text-center mb-10 text-gray-800 tracking-tight">
          Category Sales Analysis
        </h1>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {[
            { label: "Total Orders", value: totalOrders },
            { label: "Total Revenue", value: `₹${totalRevenue.toFixed(2)}` },
            { label: "Average Order Value", value: `₹${averageOrderValue.toFixed(2)}` },
          ].map((stat, index) => (
            <div
              key={index}
              className="summary-card bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl"
            >
              <h3 className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                {stat.label}
              </h3>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stat.value}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600">Loading order data...</p>
          </div>
        ) : error ? (
          <div className="text-center text-red-500 py-12 text-lg font-medium">{error}</div>
        ) : categoryDistribution.every((cat) => cat.name === "Unknown") ? (
          <div className="text-center text-red-500 py-12 text-lg font-medium">
            No valid category data found. Please ensure productIDs match category collections.
          </div>
        ) : (
          <>
            {/* Time Range Selector */}
            <div className="flex justify-center mb-10">
              <div className="inline-flex rounded-lg shadow-sm bg-white border border-gray-200">
                {["all", "year", "month"].map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`time-range-button px-5 py-2.5 text-sm font-medium ${
                      timeRange === range
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    } ${range === "all" ? "rounded-l-lg" : range === "month" ? "rounded-r-lg" : ""}`}
                  >
                    {range === "all" ? "All Time" : range === "year" ? "Last Year" : "Last Month"}
                  </button>
                ))}
              </div>
            </div>

            {/* Category Distribution Pie Chart */}
            <div className="chart-container">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Sales by Category</h2>
              {categoryDistribution.length === 0 ? (
                <p className="text-center text-gray-600">No data available for the selected time range.</p>
              ) : (
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <defs>
                        {categoryDistribution.map((_, index) => (
                          <linearGradient
                            key={`gradient-${index}`}
                            id={`gradient-${index}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].start}
                            />
                            <stop
                              offset="100%"
                              stopColor={GRADIENT_COLORS[index % GRADIENT_COLORS.length].end}
                            />
                          </linearGradient>
                        ))}
                      </defs>
                      <Pie
                        data={categoryDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={120}
                        dataKey="value"
                        animationDuration={800}
                      >
                        {categoryDistribution.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={`url(#gradient-${index})`}
                            stroke={COLORS[index % COLORS.length]}
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                      <Legend content={<CustomLegend />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Financial Trends Line Chart */}
            <div className="chart-container">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Financial Trends by Order</h2>
              {financialTrendsData.length === 0 ? (
                <p className="text-center text-gray-600">No data available for the selected time range.</p>
              ) : (
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={financialTrendsData}
                      margin={{ top: 10, right: 30, left: 20, bottom: 50 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="orderIndex" tick={<CustomTick />} height={60} />
                      <YAxis
                        label={{
                          value: "Sales (₹)",
                          angle: -90,
                          position: "insideLeft",
                          fill: "#4B5563",
                          fontSize: 14,
                        }}
                        tick={{ fontSize: 12, fill: "#4B5563" }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend content={<CustomLegend />} />
                      <Line
                        type="monotone"
                        dataKey="sales"
                        name="Sales"
                        stroke="#3B82F6"
                        strokeWidth={3}
                        dot={false}
                        activeDot={{ r: 8, fill: "#3B82F6", stroke: "#fff", strokeWidth: 2 }}
                        animationDuration={800}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Category-wise Financial Performance */}
            <div className="space-y-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Category Performance</h2>
              {Object.entries(categoryWiseData).length === 0 ? (
                <p className="text-center text-gray-600">No data available for the selected time range.</p>
              ) : (
                Object.entries(categoryWiseData)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([category, data], index) => {
                    const chartData = Object.values(data).sort((a, b) => a.orderIndex - b.orderIndex);
                    return (
                      <div key={category} className="chart-container">
                        <h3 className="text-xl font-semibold mb-6 text-gray-700">{category}</h3>
                        <div className="h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={chartData}
                              margin={{ top: 10, right: 30, left: 20, bottom: 50 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="orderIndex" tick={<CustomTick />} height={60} />
                              <YAxis
                                label={{
                                  value: "Sales (₹)",
                                  angle: -90,
                                  position: "insideLeft",
                                  fill: "#4B5563",
                                  fontSize: 14,
                                }}
                                tick={{ fontSize: 12, fill: "#4B5563" }}
                              />
                              <Tooltip content={<CustomTooltip />} />
                              <Legend content={<CustomLegend />} />
                              <Line
                                type="monotone"
                                dataKey="sales"
                                name="Sales"
                                stroke={COLORS[index % COLORS.length]}
                                strokeWidth={3}
                                dot={false}
                                activeDot={{ r: 8, fill: COLORS[index % COLORS.length], stroke: "#fff", strokeWidth: 2 }}
                                animationDuration={800}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </>
  );
}